import React, { useState, useEffect } from "react";
import { Wifi, Battery, Bluetooth, Thermometer, Waves, TreePine, Mail, Globe, ArrowUp } from "lucide-react";
import Logo from "@/components/Logo";
import { Badge } from "@/components/ui/badge";

interface Section {
  id: string;
  title: string;
  icon: React.ReactNode;
}

const sections: Section[] = [
  { id: "welcome", title: "Welcome", icon: "👋" },
  { id: "whats-in-box", title: "What's in the Box", icon: "📦" },
  { id: "how-it-works", title: "How It Works", icon: "⚡" },
  { id: "wifi-setup", title: "Wi-Fi Setup Guide", icon: <Wifi className="w-5 h-5" /> },
  { id: "reading-display", title: "Reading the Display", icon: "📺" },
  { id: "troubleshooting", title: "Troubleshooting", icon: "🔧" },
  { id: "eco-version", title: "Eco Version Note", icon: <TreePine className="w-5 h-5" /> },
  { id: "support", title: "Support", icon: "💬" },
];

const SaunaDipHelpBook: React.FC = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="max-w-4xl mx-auto p-8 bg-white font-sans">
      {/* Header */}
      <div className="text-center mb-12 border-b border-sauna-beige pb-8 sticky top-0 bg-white z-10">
        <div className="flex justify-center mb-6">
          <Logo size="md" />
        </div>
        <h1 className="text-4xl font-light text-sauna-navy mb-4">
          📘 SaunaDip Operator Help Book
        </h1>
        <p className="text-lg text-sauna-navy/70">
          Your complete guide to the SaunaDip Operator Kit
        </p>
      </div>

      {/* Table of Contents */}
      <nav className="mb-12 bg-sauna-beige/20 rounded-xl p-6">
        <h2 className="text-xl font-medium text-sauna-navy mb-4">Table of Contents</h2>
        <ul className="grid md:grid-cols-2 gap-2">
          {sections.map((section) => (
            <li key={section.id}>
              <a
                href={`#${section.id}`}
                className="flex items-center gap-2 text-sauna-navy hover:text-sauna-blue transition-colors"
              >
                <span className="text-lg">{section.icon}</span>
                {section.title}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-sauna-navy text-white p-3 rounded-full shadow-lg hover:bg-sauna-blue transition-colors z-50"
          aria-label="Back to top"
        >
          <ArrowUp className="w-6 h-6" />
        </button>
      )}

      {/* Welcome Section */}
      <section id="welcome" className="mb-12 scroll-mt-32">
        <h2 className="text-2xl font-medium text-sauna-navy mb-4 flex items-center gap-3">
          <span className="text-2xl">👋</span> Welcome
        </h2>
        <div className="bg-gradient-to-r from-sauna-blue/10 to-sauna-orange/10 rounded-2xl p-6">
          <p className="text-sauna-navy leading-relaxed mb-4">
            Welcome to SaunaDip! You're now equipped with Ireland's most trusted temperature monitoring system, 
            designed specifically for outdoor sauna and cold plunge environments.
          </p>
          <p className="text-sauna-navy/80">
            Built for the wild Irish coast, tested in all weather conditions.
          </p>
        </div>
      </section>

      {/* What's in the Box */}
      <section id="whats-in-box" className="mb-12 scroll-mt-32">
        <h2 className="text-2xl font-medium text-sauna-navy mb-6 flex items-center gap-3">
          <span className="text-2xl">📦</span> What's in the Box
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 bg-sauna-beige/50 rounded-xl">
              <div className="w-12 h-12 bg-sauna-navy rounded-lg flex items-center justify-center">
                <Thermometer className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-medium text-sauna-navy">SaunaDip Wall Unit</h3>
                <p className="text-sm text-sauna-navy/70">OLED display with Bluetooth receiver</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 p-4 bg-sauna-blue/10 rounded-xl">
              <div className="w-12 h-12 bg-sauna-blue rounded-lg flex items-center justify-center">
                <Waves className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-medium text-sauna-navy">Duck Temperature Floater</h3>
                <p className="text-sm text-sauna-navy/70">Waterproof Bluetooth sensor</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 p-4 bg-sauna-orange/10 rounded-xl">
              <div className="w-12 h-12 bg-sauna-orange rounded-lg flex items-center justify-center">
                <Battery className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-medium text-sauna-navy">Charging Cable</h3>
                <p className="text-sm text-sauna-navy/70">USB-C charging for duck sensor</p>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 bg-sauna-beige/50 rounded-xl">
              <div className="w-12 h-12 bg-sauna-navy/20 rounded-lg flex items-center justify-center">
                <span className="text-lg">🔧</span>
              </div>
              <div>
                <h3 className="font-medium text-sauna-navy">Mounting Screws</h3>
                <p className="text-sm text-sauna-navy/70">Weather-resistant hardware</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 p-4 bg-sauna-beige/50 rounded-xl">
              <div className="w-12 h-12 bg-sauna-navy/20 rounded-lg flex items-center justify-center">
                <span className="text-lg">🏃‍♂️</span>
              </div>
              <div>
                <h3 className="font-medium text-sauna-navy">Quick Start Setup Card</h3>
                <p className="text-sm text-sauna-navy/70">Laminated reference guide</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="mb-12 scroll-mt-32">
        <h2 className="text-2xl font-medium text-sauna-navy mb-6 flex items-center gap-3">
          <span className="text-2xl">⚡</span> How It Works
        </h2>
        <div className="bg-gradient-to-br from-sauna-navy/5 to-sauna-blue/10 rounded-2xl p-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="w-16 h-16 bg-sauna-orange rounded-full flex items-center justify-center mx-auto mb-4">
                <Thermometer className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-medium text-sauna-navy mb-2">Sauna Sensor</h3>
              <p className="text-sm text-sauna-navy/70">One device monitors sauna temperature</p>
            </div>
            
            <div>
              <div className="w-16 h-16 bg-sauna-blue rounded-full flex items-center justify-center mx-auto mb-4">
                <Waves className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-medium text-sauna-navy mb-2">Duck Floater</h3>
              <p className="text-sm text-sauna-navy/70">Floats in plunge pool measuring water temp</p>
            </div>
            
            <div>
              <div className="w-16 h-16 bg-sauna-navy rounded-full flex items-center justify-center mx-auto mb-4">
                <Bluetooth className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-medium text-sauna-navy mb-2">Live Updates</h3>
              <p className="text-sm text-sauna-navy/70">Transmits temperature every 10 minutes</p>
            </div>
          </div>
        </div>
      </section>

      {/* Wi-Fi Setup Guide */}
      <section id="wifi-setup" className="mb-12 scroll-mt-32">
        <h2 className="text-2xl font-medium text-sauna-navy mb-6 flex items-center gap-3">
          <Wifi className="w-7 h-7 text-sauna-blue" /> Wi-Fi Setup Guide
        </h2>
        <div className="space-y-6">
          <div className="flex gap-4">
            <Badge className="bg-sauna-orange text-white font-bold min-w-8 h-8 flex items-center justify-center">1</Badge>
            <div>
              <h3 className="font-medium text-sauna-navy mb-2">Power On</h3>
              <p className="text-sauna-navy/70">Press and hold the power button until the OLED display lights up.</p>
            </div>
          </div>
          
          <div className="flex gap-4">
            <Badge className="bg-sauna-orange text-white font-bold min-w-8 h-8 flex items-center justify-center">2</Badge>
            <div>
              <h3 className="font-medium text-sauna-navy mb-2">Connect to Setup Network</h3>
              <p className="text-sauna-navy/70">On your phone, connect to the "SaunaDip_Setup" Wi-Fi network.</p>
            </div>
          </div>
          
          <div className="flex gap-4">
            <Badge className="bg-sauna-orange text-white font-bold min-w-8 h-8 flex items-center justify-center">3</Badge>
            <div>
              <h3 className="font-medium text-sauna-navy mb-2">Access Setup Page</h3>
              <p className="text-sauna-navy/70">Visit <code className="bg-sauna-beige px-2 py-1 rounded">http://192.168.4.1</code> or scan the QR code on your device.</p>
            </div>
          </div>
          
          <div className="flex gap-4">
            <Badge className="bg-sauna-orange text-white font-bold min-w-8 h-8 flex items-center justify-center">4</Badge>
            <div>
              <h3 className="font-medium text-sauna-navy mb-2">Enter Credentials</h3>
              <p className="text-sauna-navy/70">Input your Wi-Fi network name, password, and email address for notifications.</p>
            </div>
          </div>
          
          <div className="flex gap-4">
            <Badge className="bg-sauna-green text-white font-bold min-w-8 h-8 flex items-center justify-center">✓</Badge>
            <div>
              <h3 className="font-medium text-sauna-navy mb-2">Confirmation</h3>
              <p className="text-sauna-navy/70">You'll see a success message when setup is complete. Your device will restart and connect to your network.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Reading the Display */}
      <section id="reading-display" className="mb-12 scroll-mt-32">
        <h2 className="text-2xl font-medium text-sauna-navy mb-6 flex items-center gap-3">
          <span className="text-2xl">📺</span> Reading the Display
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-sauna-blue/10 rounded-xl p-6">
            <h3 className="font-medium text-sauna-navy mb-4 flex items-center gap-2">
              <div className="w-4 h-4 bg-sauna-blue rounded"></div>
              Cold Temperatures
            </h3>
            <p className="text-sauna-navy/70 mb-2">Plunge pool temperatures display in blue</p>
            <div className="bg-white rounded-lg p-3 text-center">
              <span className="text-2xl font-mono font-bold text-sauna-blue">4°C</span>
            </div>
          </div>
          
          <div className="bg-sauna-orange/10 rounded-xl p-6">
            <h3 className="font-medium text-sauna-navy mb-4 flex items-center gap-2">
              <div className="w-4 h-4 bg-sauna-orange rounded"></div>
              Hot Temperatures
            </h3>
            <p className="text-sauna-navy/70 mb-2">Sauna temperatures display in orange</p>
            <div className="bg-white rounded-lg p-3 text-center">
              <span className="text-2xl font-mono font-bold text-sauna-orange">85°C</span>
            </div>
          </div>
        </div>
        
        <div className="mt-6 space-y-4">
          <div className="flex items-center gap-4">
            <span className="text-2xl">📶</span>
            <div>
              <h3 className="font-medium text-sauna-navy">Signal Strength</h3>
              <p className="text-sauna-navy/70">Signal icon shows Bluetooth connection strength from duck sensor</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-2xl">🔢</span>
            <div>
              <h3 className="font-medium text-sauna-navy">Multiple Sensors</h3>
              <p className="text-sauna-navy/70">One screen can display up to 5 plunge sensors (Dip1–Dip5)</p>
            </div>
          </div>
        </div>
      </section>

      {/* Troubleshooting */}
      <section id="troubleshooting" className="mb-12 scroll-mt-32">
        <h2 className="text-2xl font-medium text-sauna-navy mb-6 flex items-center gap-3">
          <span className="text-2xl">🔧</span> Troubleshooting
        </h2>
        <div className="space-y-4">
          <div className="border border-sauna-beige rounded-xl p-6">
            <h3 className="font-medium text-sauna-navy mb-2 flex items-center gap-2">
              <Wifi className="w-5 h-5 text-sauna-blue" />
              No Wi-Fi Connection?
            </h3>
            <p className="text-sauna-navy/70">Reboot the device by holding the power button for 10 seconds, then reconnect following the setup guide.</p>
          </div>
          
          <div className="border border-sauna-beige rounded-xl p-6">
            <h3 className="font-medium text-sauna-navy mb-2 flex items-center gap-2">
              <Waves className="w-5 h-5 text-sauna-blue" />
              Duck Not Showing?
            </h3>
            <p className="text-sauna-navy/70">Check battery level by pressing the button on the duck. Ensure it's within 30m range of the wall unit.</p>
          </div>
          
          <div className="border border-sauna-beige rounded-xl p-6">
            <h3 className="font-medium text-sauna-navy mb-2 flex items-center gap-2">
              <Battery className="w-5 h-5 text-sauna-orange" />
              Low Battery Warning?
            </h3>
            <p className="text-sauna-navy/70">Charge the duck sensor using the included USB-C cable. Charging takes 2-3 hours for full battery.</p>
          </div>
        </div>
      </section>

      {/* Eco Version Note */}
      <section id="eco-version" className="mb-12 scroll-mt-32">
        <h2 className="text-2xl font-medium text-sauna-navy mb-6 flex items-center gap-3">
          <TreePine className="w-7 h-7 text-sauna-green" /> Eco Version Note
        </h2>
        <div className="bg-gradient-to-r from-sauna-green/10 to-sauna-beige/50 rounded-2xl p-6">
          <p className="text-sauna-navy leading-relaxed">
            Our optional natural wooden version features no buttons — simple, sustainable, and beautifully minimal. 
            The eco version operates automatically once configured, perfect for locations prioritizing natural aesthetics.
          </p>
        </div>
      </section>

      {/* Support */}
      <section id="support" className="mb-8 scroll-mt-32">
        <h2 className="text-2xl font-medium text-sauna-navy mb-6 flex items-center gap-3">
          <span className="text-2xl">💬</span> Support
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="flex items-center gap-4 p-6 bg-sauna-navy/5 rounded-xl">
            <Mail className="w-8 h-8 text-sauna-orange" />
            <div>
              <h3 className="font-medium text-sauna-navy mb-1">Email Support</h3>
              <p className="text-sauna-blue">hello@saunadip.com</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4 p-6 bg-sauna-navy/5 rounded-xl">
            <Globe className="w-8 h-8 text-sauna-blue" />
            <div>
              <h3 className="font-medium text-sauna-navy mb-1">Online Support</h3>
              <p className="text-sauna-blue">www.saunadip.com/support</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="text-center pt-8 border-t border-sauna-beige">
        <div className="flex justify-center mb-4">
          <Logo size="sm" className="opacity-60" />
        </div>
        <p className="text-sm text-sauna-navy/60">
          Built in Ireland 🇮🇪 • © {new Date().getFullYear()} SaunaDip
        </p>
      </footer>
    </div>
  );
};

export default SaunaDipHelpBook;
